/**
 * CSS Input Component
 *
 * A code editor component for CSS input using WordPress Code Editor.
 *
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';

import { validateCss } from '../../utils/css-validator';
import type { CssValidationError } from '../../utils/css-validator/types';
import { CssInputContainer, EditorWrapper, ErrorMessage } from './styled';
import type { CssInputProps } from './types';

/**
 * CodeMirror Editor Instance Interface
 *
 * @since 1.0.0
 */
interface CodeMirrorInstance {
  getValue: () => string;
  setValue: (value: string) => void;
  on: (event: string, callback: (cm: CodeMirrorInstance) => void) => void;
  setOption: (option: string, value: boolean | string | number) => void;
  toTextArea: () => void;
}

/**
 * WordPress Code Editor Instance Interface
 *
 * @since 1.0.0
 */
interface WordPressCodeEditorInstance {
  codemirror: CodeMirrorInstance;
}

/**
 * CSS Input component using WordPress Code Editor.
 *
 * @since 1.0.0
 *
 * @param props The component props.
 *
 * @return JSX element.
 */
export const CssInput = ({
  value,
  onChange,
  disabled = false,
  height = '200px',
  className = '',
  onValidationChange,
  showErrors = true,
}: CssInputProps): JSX.Element => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorInstanceRef = useRef<WordPressCodeEditorInstance | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [validationErrors, setValidationErrors] = useState<CssValidationError[]>([]);

  // Validate CSS with debouncing
  const validateCssValue = useCallback(
    (cssValue: string) => {
      // Clear existing validation timeout
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      // Debounce validation to avoid excessive calls
      validationTimeoutRef.current = setTimeout(() => {
        const validationResult = validateCss(cssValue);
        setValidationErrors(validationResult.errors);

        // Call validation change callback if provided
        if (onValidationChange) {
          onValidationChange(validationResult.errors);
        }
      }, 500); // 500ms debounce
    },
    [onValidationChange]
  );

  // Handle editor change with proper typing and validation
  const handleChange = useCallback(
    (newValue: string) => {
      if (!(disabled ?? false) && !isInitializingRef.current) {
        // Validate the CSS
        const validationResult = validateCss(newValue);

        // Only call onChange if CSS is valid or if we want to allow saving invalid CSS
        // For this requirement, we should not save if there's an error
        onChange(newValue, validationResult.isValid);

        // Run debounced validation for UI feedback
        validateCssValue(newValue);
      }
    },
    [disabled, onChange, validateCssValue]
  );

  // Initialize WordPress Code Editor
  useEffect(() => {
    if (!textareaRef.current || !window.wp?.codeEditor || editorInstanceRef.current) {
      return;
    }

    // Prevent multiple initializations
    isInitializingRef.current = true;

    // Get WordPress Code Editor settings from localized data or use defaults
    const wpSettings = window.redfoxAddedToCartPopupSettings?.codeEditorSettings ?? {};

    // Merge WordPress settings with component-specific settings
    const settings = {
      ...wpSettings,
      codemirror: {
        ...wpSettings.codemirror,
        readOnly: disabled ?? false, // Override readOnly based on component prop
      },
    };

    // Use setTimeout to ensure DOM is ready and prevent timing issues
    timeoutRef.current = setTimeout(() => {
      try {
        if (!textareaRef.current || !window.wp?.codeEditor) {
          isInitializingRef.current = false;
          return;
        }

        const editorInstance = window.wp.codeEditor.initialize(
          textareaRef.current,
          settings
        ) as unknown as WordPressCodeEditorInstance;
        editorInstanceRef.current = editorInstance;

        if (editorInstance?.codemirror) {
          // Set initial value without triggering onChange
          if (value ?? '') {
            editorInstance.codemirror.setValue(value ?? '');
          }

          // Handle changes after initialization is complete
          editorInstance.codemirror.on('change', (cm: CodeMirrorInstance) => {
            if (!isInitializingRef.current) {
              const newValue = cm.getValue();
              handleChange(newValue);
            }
          });

          // Mark initialization as complete
          isInitializingRef.current = false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('WordPress Code Editor initialization failed:', error);
        isInitializingRef.current = false;
      }
    }, 0);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }

      if (editorInstanceRef.current?.codemirror) {
        try {
          editorInstanceRef.current.codemirror.toTextArea();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('Error cleaning up CodeMirror:', error);
        }
        editorInstanceRef.current = null;
      }

      isInitializingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to prevent re-initialization - handleChange, disabled, and value are handled in separate effects

  // Update editor value when prop changes (but not during initialization)
  useEffect(() => {
    if (
      editorInstanceRef.current?.codemirror &&
      !isInitializingRef.current &&
      value !== editorInstanceRef.current.codemirror.getValue()
    ) {
      // Temporarily disable change handling to prevent loops
      const wasInitializing = isInitializingRef.current;
      isInitializingRef.current = true;

      editorInstanceRef.current.codemirror.setValue(value ?? '');

      // Re-enable change handling
      isInitializingRef.current = wasInitializing;
    }
  }, [value]);

  // Update read-only state when disabled prop changes
  useEffect(() => {
    if (editorInstanceRef.current?.codemirror) {
      editorInstanceRef.current.codemirror.setOption('readOnly', disabled ?? false);
    }
  }, [disabled]);

  // Validate initial value
  useEffect(() => {
    if (value) {
      validateCssValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - validateCssValue is intentionally excluded to avoid re-runs

  return (
    <CssInputContainer data-disabled={(disabled ?? false).toString()} className={className ?? ''}>
      <EditorWrapper>
        <textarea
          ref={textareaRef}
          defaultValue={value ?? ''}
          style={{
            width: '100%',
            height: height ?? '200px',
            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
            fontSize: '14px',
            lineHeight: '1.5',
          }}
          disabled={disabled ?? false}
          placeholder={__('Enter your custom CSS here...', 'redfox-added-to-cart-popup')}
        />
      </EditorWrapper>
      {showErrors && validationErrors.length > 0 && (
        <div>
          {validationErrors.map((error, index) => (
            <ErrorMessage
              key={index}
              data-error-type={error.type ?? 'error'}
              data-error-source={error.source ?? 'internal'}
            >
              {__('Line', 'redfox-added-to-cart-popup')} {error.line}
              {error.col ? `, ${__('Column', 'redfox-added-to-cart-popup')} ${error.col}` : ''}
              {': '}
              {error.message}
              {error.rule && (
                <span style={{ fontSize: '0.85em', opacity: 0.7, marginLeft: '8px' }}>
                  ({error.rule.name})
                </span>
              )}
            </ErrorMessage>
          ))}
        </div>
      )}
    </CssInputContainer>
  );
};
